import React, { useCallback, useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../framework/customStore";
import { saveGeofenceThunk, setDrawingMode } from "../../../store/geofenceSlice";
import { GeofenceDto, GeofenceShapeType, SaveGeofenceDto } from "../../../models/geofence/geofence";
import { getCostCenterItems } from "../../../services/costCenterService";
import { getCustomerIdTitles } from "../../../services/customerService";

interface CustomerOption {
    id: string;
    label: string;
}

interface CostCenterOption {
    id: string;
    label: string;
    customerId: string;
}

interface GeofenceSaveDialogProps {
    open: boolean;
    shapeType: GeofenceShapeType;
    shapeData: string;
    editGeofence?: GeofenceDto | null;
    onClose: () => void;
}

export const GeofenceSaveDialog = ({
    open,
    shapeType,
    shapeData,
    editGeofence,
    onClose,
}: GeofenceSaveDialogProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const saving = useAppSelector((s) => s.geofence.saving);

    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [customer, setCustomer] = useState<CustomerOption | null>(null);
    const [costCenter, setCostCenter] = useState<CostCenterOption | null>(null);
    const [allCustomers, setAllCustomers] = useState<CustomerOption[]>([]);
    const [allCostCenters, setAllCostCenters] = useState<CostCenterOption[]>([]);
    const [optionsLoading, setOptionsLoading] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [optionsError, setOptionsError] = useState(false);

    useEffect(() => {
        if (!open) return;
        setOptionsLoading(true);
        setOptionsError(false);
        Promise.all([
            getCustomerIdTitles(),
            getCostCenterItems(9999, 1, "", "name", true, false),
        ])
            .then(([customerRes, ccRes]) => {
                setAllCustomers(
                    (customerRes?.items ?? []).map((c) => ({
                        id: c.id,
                        label: c.title,
                    }))
                );
                setAllCostCenters(
                    (ccRes?.items ?? []).map((c) => ({
                        id: c.id,
                        label: c.name,
                        customerId: c.customerId,
                    }))
                );
            })
            .catch(() => setOptionsError(true))
            .finally(() => setOptionsLoading(false));
    }, [open]);

    useEffect(() => {
        if (editGeofence) {
            setName(editGeofence.name ?? "");
            setDescription(editGeofence.description ?? "");
            setCustomer(
                editGeofence.customerId
                    ? { id: editGeofence.customerId, label: editGeofence.customerName ?? "" }
                    : null
            );
            setCostCenter(
                editGeofence.costCenterId
                    ? { id: editGeofence.costCenterId, label: editGeofence.costCenterName ?? "", customerId: editGeofence.customerId ?? "" }
                    : null
            );
        } else {
            setName("");
            setDescription("");
            setCustomer(null);
            setCostCenter(null);
        }
    }, [editGeofence, open]);

    // Cost centers filtered by selected customer — normalize GUIDs to lowercase
    const filteredCostCenters = useMemo(() => {
        if (!customer) return allCostCenters;
        const cid = customer.id.toLowerCase();
        return allCostCenters.filter((cc) => cc.customerId.toLowerCase() === cid);
    }, [allCostCenters, customer]);

    // Customers narrowed to the one that owns the selected cost center
    const filteredCustomers = useMemo(() => {
        if (!costCenter?.customerId) return allCustomers;
        const cid = costCenter.customerId.toLowerCase();
        return allCustomers.filter((c) => c.id.toLowerCase() === cid);
    }, [allCustomers, costCenter]);

    const handleCustomerChange = useCallback((val: CustomerOption | null) => {
        setCustomer(val);
        // Clear cost center if it no longer belongs to the new customer
        if (val && costCenter && costCenter.customerId.toLowerCase() !== val.id.toLowerCase()) {
            setCostCenter(null);
        }
    }, [costCenter]);

    const handleCostCenterChange = useCallback((val: CostCenterOption | null) => {
        setCostCenter(val);
        if (val?.customerId) {
            const cid = val.customerId.toLowerCase();
            const matchingCustomer = allCustomers.find((c) => c.id.toLowerCase() === cid);
            if (matchingCustomer) setCustomer(matchingCustomer);
        }
    }, [allCustomers]);

    const handleSave = useCallback(() => {
        if (!name.trim()) return;
        setSaveError(null);
        const dto: SaveGeofenceDto = {
            geofenceId: editGeofence?.geofenceId,
            name: name.trim(),
            description: description.trim() || undefined,
            customerId: customer?.id,
            costCenterId: costCenter?.id,
            shapeType,
            shapeData,
            rowId: editGeofence?.rowId,
        };
        dispatch(saveGeofenceThunk(dto)).then((result) => {
            if (saveGeofenceThunk.fulfilled.match(result)) {
                onClose();
            } else {
                setSaveError(t("monitorView.geofence.saveError"));
            }
        });
    }, [editGeofence, name, description, customer, costCenter, shapeType, shapeData, dispatch, onClose, t]);

    const handleCancel = useCallback(() => {
        dispatch(setDrawingMode(null));
        onClose();
    }, [dispatch, onClose]);

    return (
        <Dialog id="geofence-save-dialog" open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
            <DialogTitle>
                {editGeofence
                    ? t("monitorView.geofence.editTitle")
                    : t("monitorView.geofence.saveTitle")}
            </DialogTitle>
            <DialogContent>
                {optionsError && (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                        {t("monitorView.geofence.errorLoadOptions")}
                    </Alert>
                )}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
                    <TextField
                        id="geofence-name"
                        label={t("monitorView.geofence.name")}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        autoFocus
                        fullWidth
                    />
                    <TextField
                        id="geofence-description"
                        label={t("monitorView.geofence.description")}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        fullWidth
                        multiline
                        rows={2}
                    />
                    <Autocomplete
                        id="geofence-customer"
                        options={filteredCustomers}
                        loading={optionsLoading}
                        value={customer}
                        onChange={(_, val) => handleCustomerChange(val)}
                        getOptionLabel={(o) => o.label}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t("monitorView.geofence.customer")}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {optionsLoading ? <CircularProgress size={16} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                    <Autocomplete
                        id="geofence-cost-center"
                        options={filteredCostCenters}
                        loading={optionsLoading}
                        value={costCenter}
                        onChange={(_, val) => handleCostCenterChange(val)}
                        getOptionLabel={(o) => o.label}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t("monitorView.geofence.costCenter")}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {optionsLoading ? <CircularProgress size={16} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                </Box>
            </DialogContent>
            {saveError && (
                <Alert severity="error" sx={{ mx: 3, mb: 1 }}>
                    {saveError}
                </Alert>
            )}
            <DialogActions>
                <Button id="geofence-save-cancel" onClick={handleCancel} disabled={saving}>
                    {t("common.cancel")}
                </Button>
                <Button
                    id="geofence-save-submit"
                    onClick={handleSave}
                    variant="contained"
                    disabled={saving || !name.trim()}
                >
                    {saving ? <CircularProgress size={20} /> : t("common.save")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
